import { notifications } from '@mantine/notifications';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AiDigest, AiDraft, AiDraftKind, AiSummary } from '@reditelji/shared';
import { api } from './client';
import { useSession } from './session';

export type { AiDigest, AiDraft, AiSummary };

function onError(err: Error) {
  // 502/503/429 bodies already carry Slovenian copy from the API
  notifications.show({ color: 'red', title: 'AI pomočnik', message: err.message });
}

const latestSummaryKey = (volunteerId: string | null) => ['ai', 'summary-latest', volunteerId];

/**
 * The summary, regenerated by the API when the thread changed since — a Gemini
 * call, so it is asked where the thread is read (Komunikacija) and not where a
 * profile merely opens. No retries. Its answer also becomes the stored summary
 * the card on Profil reads.
 */
export function useAiSummary(volunteerId: string | null, enabled: boolean) {
  const { can } = useSession();
  const qc = useQueryClient();
  return useQuery({
    queryKey: ['ai', 'summary', volunteerId],
    queryFn: async () => {
      const s = await api<AiSummary | null>(`/volunteers/${volunteerId!}/ai/summary`);
      qc.setQueryData(latestSummaryKey(volunteerId), s);
      return s;
    },
    enabled: enabled && volunteerId !== null && can('comms:read'),
    retry: false,
    staleTime: 60_000,
  });
}

/** The stored summary as it is, however old — never generates one. */
export function useLatestAiSummary(volunteerId: string | null, enabled: boolean) {
  const { can } = useSession();
  return useQuery({
    queryKey: latestSummaryKey(volunteerId),
    queryFn: () => api<AiSummary | null>(`/volunteers/${volunteerId!}/ai/summary/latest`),
    enabled: enabled && volunteerId !== null && can('comms:read'),
    staleTime: 60_000,
  });
}

export function useAiDigest() {
  const { can } = useSession();
  return useQuery({
    queryKey: ['ai', 'digest'],
    queryFn: () => api<AiDigest | null>('/ai/digest'),
    enabled: can('comms:read'),
    retry: false,
    staleTime: 60_000,
  });
}

export function useAiMutations() {
  const qc = useQueryClient();
  const invalidateThread = () => {
    void qc.invalidateQueries({ queryKey: ['messages'] });
    void qc.invalidateQueries({ queryKey: ['volunteers'] });
    void qc.invalidateQueries({ queryKey: ['changelog'] });
    void qc.invalidateQueries({ queryKey: ['overview'] });
  };

  return {
    refreshSummary: useMutation({
      mutationFn: (volunteerId: string) =>
        api<AiSummary>(`/volunteers/${volunteerId}/ai/summary`, { method: 'POST' }),
      onSuccess: (data) => {
        qc.setQueryData(['ai', 'summary', data.volunteerId], data);
        qc.setQueryData(latestSummaryKey(data.volunteerId), data);
        void qc.invalidateQueries({ queryKey: ['changelog'] });
      },
      onError,
    }),
    createDraft: useMutation({
      mutationFn: (input: { volunteerId: string; kind: AiDraftKind; instruction: string }) =>
        api<AiDraft>(`/volunteers/${input.volunteerId}/ai/draft`, {
          method: 'POST',
          body: JSON.stringify({ kind: input.kind, instruction: input.instruction }),
        }),
      onSuccess: () => void qc.invalidateQueries({ queryKey: ['changelog'] }),
      onError,
    }),
    sendEmail: useMutation({
      mutationFn: (input: { volunteerId: string; subject: string; body: string; draftId?: string }) =>
        api(`/volunteers/${input.volunteerId}/email`, {
          method: 'POST',
          body: JSON.stringify({ subject: input.subject, body: input.body, draftId: input.draftId }),
        }),
      onSuccess: (_data, input) => {
        invalidateThread();
        void qc.invalidateQueries({ queryKey: ['ai', 'summary', input.volunteerId] });
      },
      onError: (err: Error) => notifications.show({ color: 'red', title: 'Napaka', message: err.message }),
    }),
    refreshDigest: useMutation({
      mutationFn: () => api<AiDigest>('/ai/digest', { method: 'POST' }),
      onSuccess: (data) => {
        qc.setQueryData(['ai', 'digest'], data);
        void qc.invalidateQueries({ queryKey: ['changelog'] });
      },
      onError,
    }),
  };
}
